import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../firebase/config';
import { coreApi } from '../api/api';
import { setAuthData } from '../utils/auth';
import { IS_LOCAL_HARNESS, LOCAL_CANDIDATE, LOCAL_PASSWORD, readLocalSession } from '../config/localSession';
import Navigation from '../components/Navigation';
import './Auth.css';

const Login = () => {
  // Prefilled on the local harness so the one seeded account signs in with a click.
  const [email, setEmail] = useState(IS_LOCAL_HARNESS ? LOCAL_CANDIDATE.email : '');
  const [password, setPassword] = useState(IS_LOCAL_HARNESS ? LOCAL_PASSWORD : '');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getReturnUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('returnTo') || '/';
  };

  useEffect(() => {
    const checkExistingSession = async () => {
      if (IS_LOCAL_HARNESS) {
        if (readLocalSession()) {
          navigate(getReturnUrl(), { replace: true });
          return;
        }
        setCheckingSession(false);
        return;
      }

      try {
        const response = await coreApi.get('/auth/me');
        if (response.status === 200) {
          const { email: userEmail, user_type, user_id } = response.data;
          setAuthData(userEmail, user_type, user_id);
          navigate(getReturnUrl(), { replace: true });
          return;
        }
      } catch {
        // No valid session — show login form
      }
      setCheckingSession(false);
    };
    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show error message
  const showError = (message) => {
    setErrorMessage(message);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  };

  // Show success message
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setErrorMessage('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  /**
   * The local harness stands in for core-api and Firebase, the way
   * admin-portal-frontend's VITE_E2E session does: the seeded candidate is signed in
   * outright, and the local backends take the `auth-token` the session mints. Dead
   * code in a dev or prod build.
   */
  const signInLocally = () => {
    setAuthData(LOCAL_CANDIDATE.email, LOCAL_CANDIDATE.user_type, LOCAL_CANDIDATE.user_id);
    showSuccess('Signed in to the local harness. Redirecting...');
    const returnUrl = getReturnUrl();
    setTimeout(() => navigate(returnUrl), 600);
  };

  // Verify token with backend and create session
  const verifyTokenWithBackend = async (idToken, userEmail) => {
    try {
      const response = await coreApi.post('/auth/login', {
        firebase_auth_token: idToken
      });
      
      if (response.status === 200) {
        const { user_type, user } = response.data;
        setAuthData(userEmail, user_type, user?.user_id);
        showSuccess('Login successful! Redirecting...');
        const returnUrl = getReturnUrl();
        setTimeout(() => navigate(returnUrl), 1500);
        return true;
      }
    } catch (error) {
      console.error('Login error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        error: error
      });

      if (error.response?.status === 404) {
        showError('No account found. Please sign up first.');
      } else if (error.response?.status === 401) {
        const errorData = error.response?.data;

        if (errorData?.code === 'EMAIL_NOT_VERIFIED') {
          showError('Please verify your email before logging in. Check your inbox for a verification link.');
          setShowResendVerification(true);
        } else {
          showError(errorData?.error || 'Invalid authentication token.');
        }
      } else {
        showError('Login verification failed. Please try again.');
      }
      return false;
    }
  };

  // Handle email/password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      showError('Please enter both email and password.');
      return;
    }

    if (IS_LOCAL_HARNESS) {
      signInLocally();
      return;
    }

    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      await verifyTokenWithBackend(idToken, userCredential.user.email);
    } catch (error) {
      console.error('Email login error:', error);
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          showError('Invalid email or password.');
          break;
        case 'auth/invalid-email':
          showError('Invalid email address.');
          break;
        case 'auth/too-many-requests':
          showError('Too many failed attempts. Please try again later.');
          break;
        default:
          showError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google sign-in
  const handleGoogleSignIn = async () => {
    if (IS_LOCAL_HARNESS) {
      signInLocally();
      return;
    }

    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      await verifyTokenWithBackend(idToken, result.user.email);
    } catch (error) {
      console.error('Google sign-in error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        showError('Sign-in cancelled.');
      } else if (error.code === 'auth/popup-blocked') {
        showError('Popup blocked. Please allow popups and try again.');
      } else {
        showError('Google sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
        // Send verification email through backend
        const response = await coreApi.post('/auth/resend-verification', {
            email: email
        });
        
        if (response.status === 200) {
            showSuccess(response.data.message || 'Verification email sent! Please check your inbox.');
            setShowResendVerification(false);
        } else {
            showError('Failed to send verification email. Please try again.');
        }
    } catch (error) {
        console.error('Resend verification error:', error);
        let errorMessage = 'Failed to send verification email. Please try again.';
        
        if (error.response) {
            if (error.response.status === 429) {
                // Rate limiting response
                errorMessage = error.response.data.message || 'Please wait before requesting another verification email.';
            } else if (error.response.data && error.response.data.error) {
                errorMessage = error.response.data.error;
            } else if (error.response.data && error.response.data.message) {
                errorMessage = error.response.data.message;
            }
        }
        
        showError(errorMessage);
    } finally {
        setResendLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      showError('Please enter your email address.');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      const response = await coreApi.post('/auth/forgot-password', { email });
      if (response.status === 200) {
        showSuccess(response.data.message || 'If an account with this email exists, a password reset link has been sent.');
        setShowForgotPassword(false);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      let msg = 'Failed to send reset link. Please try again.';

      if (error.response?.status === 429) {
        msg = error.response.data?.message || 'Please wait before requesting another reset link.';
      } else if (error.response?.data?.error) {
        msg = error.response.data.error;
      }

      showError(msg);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <>
        <Navigation />
        <div className="auth-page auth-page-with-nav">
          <div className="auth-container">
            <div className="auth-card">
              <div className="auth-header">
                <img src="/images/appli-logo-black.png" alt="Appli Agent Logo" className="auth-logo" />
                <p>Checking session...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="auth-page auth-page-with-nav">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <img
                src="/images/appli-logo-black.png"
                alt="Appli Agent Logo"
                className="auth-logo"
              />
              <h1>Welcome Back</h1>
              <p>Sign in to your account to continue.</p>
              {IS_LOCAL_HARNESS && (
                <p className="auth-local-note">
                  Local harness — one seeded account ({LOCAL_CANDIDATE.email}). The password is
                  not checked.
                </p>
              )}
            </div>

            {/* Error/Success Messages */}
            {errorMessage && (
              <div className="error-message" style={{ display: 'block' }}>
                {errorMessage}
              </div>
            )}
            {successMessage && (
              <div className="success-message" style={{ display: 'block' }}>
                {successMessage}
              </div>
            )}

            {/* Resend Verification Section */}
            {showResendVerification && (
              <div className="resend-verification">
                <p>Need a new verification link?</p>
                <button 
                  type="button" 
                  onClick={handleResendVerification}
                  className="resend-button"
                  disabled={resendLoading}
                >
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </div>
            )}

            {showForgotPassword ? (
              <div className="auth-form">
                <h2 style={{ fontSize: '18px', marginBottom: '8px', textAlign: 'center' }}>Reset Your Password</h2>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px', textAlign: 'center' }}>
                  Enter your email and we'll send you a link to reset your password.
                </p>
                <div className="form-field">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={forgotPasswordLoading}
                    required
                  />
                </div>
                <button
                  type="button"
                  className="auth-button"
                  onClick={handleForgotPassword}
                  disabled={forgotPasswordLoading}
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="back-btn"
                  style={{ marginTop: '12px', width: '100%' }}
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <>
                {/* Email/Password Form */}
                <form className="auth-form" onSubmit={handleEmailLogin}>
                  <div className="form-field">
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      placeholder="Email Address" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      required 
                    />
                  </div>
                  <div className="form-field">
                    <input 
                      type="password" 
                      id="password" 
                      name="password" 
                      placeholder="Password" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      required 
                    />
                  </div>
                  <div style={{ textAlign: 'right', marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#4a90d9',
                        cursor: 'pointer',
                        fontSize: '13px',
                        padding: 0,
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <button 
                    type="submit" 
                    className="auth-button"
                    disabled={loading}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </button>
                </form>

                {/* Divider */}
                <div className="auth-divider">
                  <span>or</span>
                </div>

                {/* Google Sign-in Button */}
                <button 
                  type="button" 
                  onClick={handleGoogleSignIn}
                  className="google-signin-button"
                  disabled={loading}
                >
                  <svg className="google-icon" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {loading ? 'Signing In...' : 'Continue with Google'}
                </button>
              </>
            )}

            <div className="auth-footer">
              <p>
                Don't have an account? <Link to="/signup">Sign Up</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
